import React, { useState, useEffect } from "react";
import tw from "twin.macro";
import styled from "styled-components";
import { getAuth } from "firebase/auth";
import { fetchTransactionHistory } from "services/backendCalls";
import Currency from "react-currency-formatter";
import AnimationRevealPage from "helpers/AnimationRevealPage.js";
import Header from "components/Navbar.js";
import { useHistory } from "react-router-dom";
import cogoToast from "cogo-toast";
import { connect } from 'react-redux';
import { addToBasket } from 'services/actions.js';
import ReviewForm from 'components/forms/ReviewForm.js';

const Container = tw.div`relative -mx-8 mt-0 bg-gray-100 min-h-screen`;
const ContentContainer = tw.div`max-w-screen-xl mx-auto py-20 lg:py-24 px-4`;
const Heading = tw.h1`text-4xl sm:text-5xl font-black text-green-600 mb-8`;
const TransactionCard = tw.div`bg-white rounded-lg shadow-lg p-6 mb-6 hover:shadow-xl transition-shadow duration-300`;
const TransactionHeader = tw.div`flex justify-between items-start mb-4 pb-4 border-b border-gray-200`;
const TransactionId = tw.p`text-sm text-gray-600 font-mono`;
const TransactionDate = tw.p`text-sm text-gray-500`;
const StatusBadge = styled.span`
  ${tw`px-3 py-1 rounded-full text-xs font-semibold uppercase`}
  ${props => props.status === 'success' ? tw`bg-green-100 text-green-800` : tw`bg-red-100 text-red-800`}
`;
const ItemsList = tw.div`space-y-3 mb-4`;
const ItemRow = tw.div`flex items-start justify-between py-2 border-b border-gray-100`;
const ItemImage = tw.img`w-16 h-16 object-cover rounded`;
const ItemDetails = tw.div`flex-1 ml-4`;
const ItemTitle = tw.h4`font-semibold text-gray-800`;
const ItemQuantity = tw.p`text-sm text-gray-600`;
const BasketDetails = tw.div`mt-2 space-y-1`;
const BasketItem = tw.p`text-sm text-gray-600 flex items-center`;
const CheckIcon = tw.span`text-green-500 mr-2`;
const ItemPrice = tw.p`font-semibold text-gray-800`;
const TotalSection = tw.div`flex justify-between items-center pt-4 border-t-2 border-gray-300`;
const TotalLabel = tw.span`text-lg font-bold text-gray-800`;
const TotalValue = tw.span`text-xl font-bold text-green-600`;
const ReorderButton = tw.button`mt-4 w-full bg-green-600 text-white py-3 rounded-lg font-semibold hover:bg-green-700 transition-all duration-300`;
const EmptyState = tw.div`text-center py-20`;
const EmptyStateText = tw.p`text-xl text-gray-500 mb-4`;
const ShopButton = tw.button`bg-green-600 text-white px-8 py-3 rounded-lg font-semibold hover:bg-green-700 transition-all duration-300`;
const LoadingSpinner = tw.div`flex justify-center items-center py-20`;
const ErrorMessage = tw.div`bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded mb-4`;
const FilterSection = tw.div`mb-6 flex gap-4 flex-wrap`;
const FilterButton = styled.button`
  ${tw`px-4 py-2 rounded-lg font-medium transition-all duration-300`}
  ${props => props.active ? tw`bg-green-600 text-white` : tw`bg-white text-gray-700 hover:bg-gray-100`}
`;
const ReviewBtn = tw.button`text-xs bg-white border border-green-600 text-green-600 hover:bg-green-600 hover:text-white font-semibold py-1 px-3 rounded transition-colors mt-2`;

const TransactionHistoryComponent = (props) => {
  const [transactions, setTransactions] = useState([]);
  const [allTransactions, setAllTransactions] = useState([]); // Store all for count
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [filter, setFilter] = useState('all'); // all, recent, month
  const [user, setUser] = useState(null);
  const [authLoading, setAuthLoading] = useState(true);
  const [reviewingItem, setReviewingItem] = useState(null);
  const auth = getAuth();
  const history = useHistory();

  // Listen for auth state changes
  useEffect(() => {
    const unsubscribe = auth.onAuthStateChanged((currentUser) => {
      console.log('[TransactionHistory] Auth state changed:', currentUser?.uid);
      setUser(currentUser);
      setAuthLoading(false);
    });

    return () => unsubscribe();
  }, [auth]);

  useEffect(() => {
    if (!authLoading) {
      fetchTransactions();
    }
  }, [user, filter, authLoading]);

  const fetchTransactions = async (attempt = 1) => {
    let willRetry = false;
    if (!user) {
      setLoading(false);
      return;
    }

    try {
      setLoading(true);
      setError(null);
      
      // First, fetch all transactions to get the count
      const allData = await fetchTransactionHistory(user.uid, {});
      setAllTransactions(allData);
      console.log('[TransactionHistory] All transactions:', allData.length);
      
      // Then apply client-side filtering for "This Month" since REST API might not handle string timestamps well
      let filteredData = allData;
      
      if (filter === 'recent') {
        filteredData = allData.slice(0, 5);
      } else if (filter === 'month') {
        const oneMonthAgo = new Date();
        oneMonthAgo.setMonth(oneMonthAgo.getMonth() - 1);
        
        filteredData = allData.filter(transaction => {
          // Handle both ISO string and Firestore timestamp formats
          const txDate = transaction.timestamp 
            ? new Date(transaction.timestamp)
            : new Date(transaction.createdAt);
          return txDate >= oneMonthAgo;
        });
        console.log('[TransactionHistory] This month transactions:', filteredData.length, 'of', allData.length);
      }

      setTransactions(filteredData);
    } catch (err) {
      console.error("Error fetching transactions:", err);
      // auth/network-request-failed is transient (Firebase STS token refresh
      // dropped at the network layer). Retry with backoff before giving up.
      const isNetworkError =
        err && (err.code === 'auth/network-request-failed' || err.message?.includes('network'));
      if (isNetworkError && attempt < 3) {
        const delay = attempt * 1500;
        console.log(`[TransactionHistory] Network error, retrying in ${delay}ms (attempt ${attempt})`);
        setError("Connection issue — retrying…");
        willRetry = true;
        setTimeout(() => fetchTransactions(attempt + 1), delay);
        return;
      }
      setError("Failed to load transaction history. Please check your connection and try again.");
    } finally {
      // Keep the spinner up while a retry is still pending.
      if (!willRetry) {
        setLoading(false);
      }
    }
  };

  const handleRetry = () => {
    setError(null);
    fetchTransactions(1);
  };

  const handleReorder = (transaction) => {
    // Reorder only applies to cart-based transactions that have items
    if (!transaction.items || transaction.items.length === 0) {
      cogoToast.info('Reorder is only available for store purchases.');
      return;
    }
    // Add all items from the transaction back to the basket
    transaction.items.forEach(item => {
      props.addToBasket(item);
    });
    
    cogoToast.success(`${transaction.items.length} item(s) added to basket`);
    history.push('/checkout');
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  if (authLoading || loading) {
    return (
      <AnimationRevealPage>
        <Header />
        <Container>
          <ContentContainer>
            <LoadingSpinner>
              <div className="spinner-border text-primary" role="status">
                <span className="sr-only">Loading...</span>
              </div>
            </LoadingSpinner>
          </ContentContainer>
        </Container>
      </AnimationRevealPage>
    );
  }

  if (!user) {
    return (
      <AnimationRevealPage>
        <Header />
        <Container>
          <ContentContainer>
            <EmptyState>
              <EmptyStateText>Please sign in to view your transaction history</EmptyStateText>
              <ShopButton onClick={() => history.push('/login')}>
                Sign In
              </ShopButton>
            </EmptyState>
          </ContentContainer>
        </Container>
      </AnimationRevealPage>
    );
  }

  return (
    <AnimationRevealPage>
      <Header />
      <Container>
        <ContentContainer>
          <Heading>Transaction History</Heading>

          <FilterSection>
            <FilterButton 
              active={filter === 'all'} 
              onClick={() => setFilter('all')}
            >
              All Transactions {allTransactions.length > 0 && `(${allTransactions.length})`}
            </FilterButton>
            <FilterButton 
              active={filter === 'recent'} 
              onClick={() => setFilter('recent')}
            >
              Recent {allTransactions.length > 0 && `(${Math.min(5, allTransactions.length)})`}
            </FilterButton>
            <FilterButton 
              active={filter === 'month'} 
              onClick={() => setFilter('month')}
            >
              This Month
            </FilterButton>
          </FilterSection>

          {error && (
            <ErrorMessage>
              {error}
              {!/retrying/i.test(error) && (
                <button
                  type="button"
                  onClick={handleRetry}
                  style={{ marginLeft: '0.75rem', textDecoration: 'underline', fontWeight: 600 }}
                >
                  Retry
                </button>
              )}
            </ErrorMessage>
          )}

          {loading ? (
            <LoadingSpinner>
              <div className="spinner-border text-primary" role="status">
                <span className="sr-only">Loading...</span>
              </div>
            </LoadingSpinner>
          ) : !error && transactions.length === 0 ? (
            <EmptyState>
              <EmptyStateText>No transactions found</EmptyStateText>
              <ShopButton onClick={() => history.push('/marketplace')}>
                Start Shopping
              </ShopButton>
            </EmptyState>
          ) : (
            transactions.map((transaction) => (
              <TransactionCard key={transaction.id || transaction.firestoreId}>
                <TransactionHeader>
                  <div>
                    <TransactionId>
                      Transaction ID: {transaction.reference}
                    </TransactionId>
                    <TransactionDate>
                      {formatDate(transaction.createdAt)}
                    </TransactionDate>
                  </div>
                  <StatusBadge status={transaction.status}>
                    {transaction.status}
                  </StatusBadge>
                </TransactionHeader>

                <ItemsList>
                  {((transaction.items && transaction.items.length > 0)
                    ? transaction.items
                    : (transaction.type === 'co_buy_contribution' || transaction.groupName
                        ? [{
                            title: transaction.groupName || 'Co-Buy Contribution',
                            quantity: 1,
                            price: transaction.contribution || 0,
                            total: transaction.contribution || 0,
                            imageSrc: '',
                            basket: []
                          }]
                        : [])
                  ).map((item, index) => (
                    <ItemRow key={index}>
                      {item.imageSrc && (
                        <ItemImage src={item.imageSrc} alt={item.title} />
                      )}
                      <ItemDetails>
                        <ItemTitle>{item.title}</ItemTitle>
                        {item.basket && item.basket.length > 0 ? (
                          <BasketDetails>
                            {item.basket.map((basketItem, idx) => (
                              <BasketItem key={idx}>
                                <CheckIcon>✓</CheckIcon>
                                {basketItem.quantity} {basketItem.measure}(s) of {basketItem.type} @₦{basketItem.price} each (₦{basketItem.total})
                              </BasketItem>
                            ))}
                          </BasketDetails>
                        ) : (
                          <ItemQuantity>Quantity: {item.quantity || 1}</ItemQuantity>
                        )}
                      </ItemDetails>
                      <ItemPrice>
                        <Currency quantity={item.total || item.price} currency="NGN" />
                        <div tw="text-right">
                          <ReviewBtn onClick={() => setReviewingItem(item)}>Review</ReviewBtn>
                        </div>
                      </ItemPrice>
                    </ItemRow>
                  ))}
                </ItemsList>

                <TotalSection>
                  <TotalLabel>Total Paid</TotalLabel>
                  <TotalValue>
                    <Currency quantity={transaction.total} currency="NGN" />
                  </TotalValue>
                </TotalSection>

                <ReorderButton onClick={() => handleReorder(transaction)}>
                  Reorder Items
                </ReorderButton>
              </TransactionCard>
            ))
          )}
        </ContentContainer>
      </Container>
      {reviewingItem && (
        <ReviewForm item={reviewingItem} onClose={() => setReviewingItem(null)} />
      )}
    </AnimationRevealPage>
  );
};

const TransactionHistory = connect(
  null,
  (dispatch) => ({
    addToBasket: (product) => {
      dispatch(addToBasket(product));
    }
  })
)(TransactionHistoryComponent);

export default TransactionHistory;
